import { ArpMedia, PaginatedResponse } from '@arpeggi/shared'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  UseQueryOptions
} from 'react-query'
import { queryClient } from '../../features/react-query-provider'
import {
  getArpMedia,
  getArpMediaNotifications,
  GetArpMediaNotificationsParams,
  GetArpMediaParams,
  getArpMediaStatistics,
  getOneArpMedia,
  Parts,
  UpdateArpMediaDto,
  useArpMediaAPI
} from '../api/v2/ArpMediaAPI'
import { getNextPage, getPreviousPage } from './common'

export const useArpMedias = (
  params?: GetArpMediaParams,
  options?: UseInfiniteQueryOptions<PaginatedResponse<ArpMedia>>
) => {
  return useInfiniteQuery(
    ['arp-media', params],
    ({ pageParam = params?.page || 1 }) => getArpMedia({ ...params, page: pageParam }),
    {
      getPreviousPageParam: (firstPage) => getPreviousPage(firstPage),
      getNextPageParam: (lastPage) => getNextPage(lastPage),
      ...options
    }
  )
}

export const useArpMedia = ({
  id,
  params,
  options
}: {
  id: number
  params?: { parts?: Parts }
  options?: UseQueryOptions<ArpMedia>
}) => {
  return useQuery(['arp-media', id, params], () => getOneArpMedia(id, params), options)
}

export const useUpdateArpMedia = (id: number) => {
  const { updateArpMedia } = useArpMediaAPI()

  return useMutation((dto: UpdateArpMediaDto) => updateArpMedia(id, dto), {
    onSuccess: () => {
      queryClient.invalidateQueries(['arp-media'])
      queryClient.invalidateQueries(['arp-media', id])
    }
  })
}

export const useArpMediaStatistics = () => {
  return useQuery(['arp-media-statistics'], () => getArpMediaStatistics())
}

export const useArpMediaNotifications = ({
  id,
  params
}: {
  id: number
  params?: GetArpMediaNotificationsParams
}) => {
  return useInfiniteQuery(
    ['arp-media', id, 'notifications', params],
    ({ pageParam = params?.page || 1 }) =>
      getArpMediaNotifications(id, { ...params, page: pageParam }),
    {
      getPreviousPageParam: (firstPage) => getPreviousPage(firstPage),
      getNextPageParam: (lastPage) => getNextPage(lastPage)
    }
  )
}
